import React, { Fragment, useEffect, useRef, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import {
  WorkOrderDetail,
  WorkOrderOutbound
} from '../../interfaces/WorkOrderOutbound'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { useModal } from '../../hooks/useModal'
import { usePagination } from '../../hooks/usePagination'
import { Status } from '../../hooks/useFetchState'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { generateTableHeadersWorkOrderOutbound } from './config/index'
import { useWorkOrderState } from '../../hooks'
import { CreatePickingRouteModal } from './modal/CreatePickingRouteModal'
import { PickingRoute } from '../../interfaces/Picking/Picking'
import { DeleteWorkOrderModal } from './modal'
import { getWorkOrderOutbounds } from './config/api'
import { WorkOrderOutboundButtonBar } from './WorkOrderOutboundButtonBar'

import { useCourier } from '../../hooks/useCourier'
import { BlogWorkOrderModal } from './modal/BlogWorkOrderModal'
import { EditWorkOrderModalComponent } from './modal/EditWorkOrderModal'
import {
  GenericOrderBy,
  getNewDirection,
  GenericTable
} from '../../components/_EOS/Table'
import { usePriorityManagement } from './hooks/usePriorityManagement'
import { Filter } from '../../components/_WMS/FilterComponent'
import { FilterExtraAction } from '../../components/_WMS/FilterComponent/types'
import { WorkOrderOutboundSearch } from './search'
import { defaultFilters } from './search/config'
import { eTypeOrder } from '../../enums/eTypeOrder'
import { useTypesOrder } from '../../hooks/useTypeOrder'
import { Flag as FlagIcon } from '@mui/icons-material'
import { useProductOrigins } from '../../hooks/useProductOrigins'
import { eTypeStorage } from '../../enums/eTypeStorage'
import { useSettings } from '../../hooks/useSettings'
import { eSetting } from '../../enums/eSetting'
import { eTypeSetting } from '../../enums/eTypeSetting'

interface WorkOrderOutboundPageProps {}

export const WorkOrderOutboundPage: React.FC<
  WorkOrderOutboundPageProps
> = () => {
  const [selectedFilters, setSelectedFilters] =
    React.useState<Filter[]>(defaultFilters)
  const { state, callApi } = useApi<ApiResponse<WorkOrderOutbound>>()

  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const [selectedElement, setSelectedElement] = React.useState<number[]>([])
  const [isPriorizing, setIsPriorizing] = React.useState<boolean>(false)

  const {
    productOriginsState: { productOrigins },
    getProductOrigins
  } = useProductOrigins()
  useEffect(() => {
    if (!productOrigins || productOrigins.length === 0) getProductOrigins()
  }, [])

  const { settingsState, getSettings } = useSettings()

  const selectedBrandId = selectedFilters?.find(
    (f) => f.key === 'productOriginId'
  )?.value

  const isInternalBrand = Boolean(
    selectedBrandId != null &&
    productOrigins?.some(
      (po) =>
        String(po.productOriginId) === String(selectedBrandId) &&
        po.typeStorageId === eTypeStorage.Interno
    )
  )

  useEffect(() => {
    if (selectedBrandId != null && isInternalBrand) {
      getSettings(eTypeSetting.Automatizaciones, selectedBrandId)
    }
  }, [selectedBrandId])

  const priorizationSettingActive =
    settingsState.settings.find(
      (s) =>
        s.settingsMetadataId === eSetting.PrioritizeWorkOrdersForInternalStorage
    )?.value === 'true'

  const canPriorize = isInternalBrand && priorizationSettingActive

  const priorizationDisabledReason = !isInternalBrand
    ? 'Disponible solo para marcas con almacenamiento interno'
    : 'La configuración de priorización para esta marca está deshabilitada'

  const editModal = useModal<{
    workOrder: WorkOrderOutbound
  }>('Editar Orden de Trabajo')

  const createPickingRouteModal = useModal<PickingRoute>(
    'Crear Rutas de Picking'
  )

  const deleteWorkOrderModal = useModal<{ workOrderId: number }>(
    'Eliminar Orden de Trabajo'
  )

  const blogWorkOrderModal = useModal<{
    workOrderId: number
    woDetail: WorkOrderDetail[]
  }>('Bitácora Orden de Trabajo')

  const pagination = usePagination(5)

  /*  ===================
        PRIORITY MANAGEMENT
    ======================= */
  const { requestUpdatePriority, state: updatePriorityState } =
    usePriorityManagement()

  const requestUpdatePriorityTop = (workOrderId: number) => {
    requestUpdatePriority('top', workOrderId, pagination, selectedFilters)
      .then(() => handleSuccessPriorization(workOrderId))
      .catch((err) => openSnackBar(err, false))
  }

  const requestUpdatePriorityUp = (workOrderId: number) => {
    requestUpdatePriority('up', workOrderId, pagination, selectedFilters)
      .then(() => handleSuccessPriorization(workOrderId))
      .catch((err) => openSnackBar(err, false))
  }

  const requestUpdatePriorityDown = (workOrderId: number) => {
    requestUpdatePriority('down', workOrderId, pagination, selectedFilters)
      .then(() => handleSuccessPriorization(workOrderId))
      .catch((err) => openSnackBar(err, false))
  }

  const handleSuccessPriorization = (workOrderId: number) => {
    openSnackBar(`Se repriorizó correctamente OT ${workOrderId}`, true)
    requestWorkOrderOutbounds(
      selectedFilters,
      pagination.page,
      pagination.pageSize
    )
  }

  const requestWorkOrderOutbounds = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getWorkOrderOutbounds(filters, page, pageSize, orderBy)).then(() =>
      setSelectedElement([])
    )
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    setSelectedElement([])
    requestWorkOrderOutbounds(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const {
    workOrderStatesState: { workOrderStates },
    getWorkOrderStates
  } = useWorkOrderState()

  const {
    typesOrderState: { typesOrder },
    getTypesOrder
  } = useTypesOrder()

  const tableHeadersWorkOrderOutbound = generateTableHeadersWorkOrderOutbound(
    workOrderStates || [],
    typesOrder || [],
    blogWorkOrderModal.openModal
  )

  const handleCheckAllToggle = (selectAll: boolean) => {
    if (selectAll) {
      // Selecciona todos los IDs de las filas actualmente visibles
      const newCheckedElements = state.data?.items.map((item) => item.id) || []
      setSelectedElement(newCheckedElements)
    } else {
      // Limpia la selección
      setSelectedElement([])
    }
  }

  const handleCheckChange = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedElement((prev) => [...prev, id])
    } else {
      setSelectedElement((prev) => [...prev.filter((el) => el !== id)])
    }
  }

  const {
    couriersState: { couriers },
    getCouriers
  } = useCourier()

  const [orderBy, setOrderBy] = useState<GenericOrderBy<WorkOrderOutbound>>({
    field: '',
    direction: 'desc'
  })

  const handleSorting = ({
    field,
    direction
  }: GenericOrderBy<WorkOrderOutbound>) => {
    const auxField = field === 'id' ? 'workOrderId' : field
    if (auxField === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((_prev) => ({ field: auxField, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  const onClickEditElement = (id: number, row: WorkOrderOutbound) =>
    editModal.openModal(
      'md',
      {
        workOrder: row
      },
      `Editar Orden de Trabajo #${
        row.workOrderDetails &&
        row.workOrderDetails.every((o) => o.typeOrderId === eTypeOrder.RTV)
          ? `RTV${id}`
          : id
      }`
    )

  useEffect(() => {
    if (workOrderStates?.length === 0) {
      getWorkOrderStates()
    }

    if (typesOrder?.length === 0) {
      getTypesOrder()
    }

    if (couriers?.length === 0) {
      getCouriers()
    }

    requestWorkOrderOutbounds(selectedFilters, 0, pagination.pageSize)
  }, [])

  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    console.log(`use orderBy`)
    pagination.setPagination(0, pagination.pageSize)
    requestWorkOrderOutbounds(selectedFilters, 0, pagination.pageSize)
  }, [orderBy])

  const extraActions: FilterExtraAction[] = [
    canPriorize
      ? {
          label: isPriorizing ? 'Terminar Priorización' : 'Priorizar',
          onClick: () => setIsPriorizing(!isPriorizing),
          icon: <FlagIcon />
        }
      : {
          label: 'Priorizar',
          onClick: () => {},
          icon: <FlagIcon />,
          disabled: true,
          tooltip: priorizationDisabledReason
        }
  ]

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        {/* SEARCH */}
        <WorkOrderOutboundSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={(f) => {
            pagination.setPagination(0, pagination.pageSize)
            requestWorkOrderOutbounds(f, 0, pagination.pageSize)
          }}
          extraActions={extraActions}
          onAddButtonClick={() => {
            if (selectedElement.length === 0) {
              openSnackBar('Debe seleccionar al menos una OT', false)
              return
            }
            createPickingRouteModal.openModal('sm')
          }}
          createButtonTooltip='Crear Ruta Picking'
        />

        {/* BUTTON BAR - Solo AlertBase */}
        <WorkOrderOutboundButtonBar
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          isPriorizing={isPriorizing}
          setIsPriorizing={setIsPriorizing}
          priorizationDisabledReason={
            !canPriorize ? priorizationDisabledReason : undefined
          }
          selectedElement={selectedElement}
          onClickDeleteOpenModalButton={deleteWorkOrderModal.openModal}
          onClickCreatePickingRouteButton={createPickingRouteModal.openModal}
          handleSearch={requestWorkOrderOutbounds}
          onClickMassiveTagModification={() => {}}
          workOrderStates={workOrderStates}
          hideButtons={true}
        />

        {/* TABLA ANTIGUA (ORIGINAL) */}

        <GenericTable<WorkOrderOutbound>
          rowConfig={state.data?.items.map((el) =>
            el?.workOrderDetails &&
            el.workOrderDetails.every((o) => o.typeOrderId === eTypeOrder.RTV)
              ? { backgroundColor: '#FCF3CF' }
              : { backgroundColor: 'white' }
          )}
          headersConfig={tableHeadersWorkOrderOutbound}
          IDKey={'id'}
          data={state.data?.items}
          actions={{
            onEdit: onClickEditElement,
            onDelete: (id) =>
              deleteWorkOrderModal.openModal('sm', { workOrderId: id }),
            onView: (_id, row) =>
              blogWorkOrderModal.openModal(
                'lg',
                { workOrderId: row.id, woDetail: row.workOrderDetails },
                `Bitácora Orden de Trabajo #${
                  row.workOrderDetails &&
                  row.workOrderDetails.every(
                    (o) => o.typeOrderId === eTypeOrder.RTV
                  )
                    ? `RTV${_id}`
                    : _id
                }`
              ),
            tooltips: {
              onEdit: 'Editar',
              onDelete: 'Eliminar',
              onView: 'Ver bitácora'
            }
          }}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={
            state.status === Status.Loading ||
            updatePriorityState.status === Status.Loading
          }
          showCheckboxes={true}
          onCheckChange={handleCheckChange}
          checkedElements={selectedElement}
          showPriorityMenu={isPriorizing}
          priorization={{
            onTop: (id) => requestUpdatePriorityTop(id),
            onUp: (id) => requestUpdatePriorityUp(id),
            onDown: (id) => requestUpdatePriorityDown(id)
          }}
          onSortChange={handleSorting}
          onCheckAll={handleCheckAllToggle}
          orderField={orderBy}
          priorizationNumberKey='rowNum'
        />
      </StandardPage>

      {/* ************ */}
      {/*    MODALS    */}
      {/* ************ */}

      {/* EDIT MODAL */}
      <EditWorkOrderModalComponent
        editModal={editModal}
        requestWorkOrderOutbounds={() =>
          requestWorkOrderOutbounds(
            selectedFilters,
            pagination.page,
            pagination.pageSize
          )
        }
      />

      {/* CREATE PICKING ROUTE MODAL */}
      <CreatePickingRouteModal
        selectedOts={selectedElement}
        createPickingRouteModal={createPickingRouteModal}
        handleRequestWorkOrders={() => {
          requestWorkOrderOutbounds(
            selectedFilters,
            pagination.page,
            pagination.pageSize
          )
        }}
        handleSnackbarNotification={openSnackBar}
      />

      {/* DELETE WORK ORDER MODAL */}
      <DeleteWorkOrderModal
        deleteWorkOrderModalState={deleteWorkOrderModal}
        handleSnackbarNotification={openSnackBar}
        handleRequestWorkOrders={() =>
          requestWorkOrderOutbounds(
            selectedFilters,
            pagination.page,
            pagination.pageSize
          )
        }
      />

      <BlogWorkOrderModal
        blogWorkOrderModalState={blogWorkOrderModal}
        handleRequestWorkOrders={() =>
          requestWorkOrderOutbounds(
            selectedFilters,
            pagination.page,
            pagination.pageSize
          )
        }
      />

      {/* SNACKBAR */}
      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
